import { Sentry, SentryTeam } from 'lib-shared-sentry';
import type { SearchFilters, SearchParameters } from 'lib-shared-types-api';
import { useCallback, useState } from 'react';
import { useEffectOnce, usePrevious, useUpdateEffect } from 'react-use';

import { CustomFieldEntity, customFieldsApi } from 'apis/customFields';
import { searchApi } from 'apis/search';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { useAppPathParams } from 'utils/url/useAppPathParams';
import { useAppSearchParams } from 'utils/url/useAppSearchParams';

import { searchActions } from '../../search.actions';
import { convertFiltersStringToObject } from '../filters/convertFilters';
import { isCustomAttributeCondition } from '../filters/filterTypeGuards';
import { convertSortStringToObject } from '../sort/convertSort';

type UseSearchInitResult = {
  isInitialized: boolean;
};

/** A hook that initializes the Search page. */
export function useSearchInit(): UseSearchInitResult {
  const dispatch = useAppDispatch();
  const { scope } = useAppPathParams('/search/:scope');
  const { params: urlParams } = useAppSearchParams('/search/:scope');
  const { params } = useAppSelector((state) => state.search);
  const prevParams = usePrevious(params);

  const [isInitialized, setIsInitialized] = useState(false);

  const [readSearchEnrichedFiltersQuery] =
    searchApi.useLazyReadSearchEnrichedFiltersQuery();

  const readCustomAttributes = useCallback(async () => {
    try {
      const entities: CustomFieldEntity[] = ['conversation', 'contact'];

      for (const entityId of entities) {
        await dispatch(
          customFieldsApi.endpoints.readAttributes.initiate(
            { entityId },
            { subscribe: true },
          ),
        ).unwrap();
      }
    } catch (error) {}
  }, [dispatch]);

  const updateSearchParams = useCallback(
    (filters: SearchFilters) => {
      const nextParams: SearchParameters = {
        scope,
        query: urlParams.q || '',
        filters,
        sort: convertSortStringToObject(urlParams.sort),
        strict: urlParams.strict === 'true' ? true : false,
        page: urlParams.page ? parseInt(urlParams.page) : 1,
      };

      dispatch(searchActions.params.update(nextParams));
    },
    [dispatch, scope, urlParams],
  );

  /**
   * Parses and enriches the filters from the URL params if necessary, then
   * updates the search Redux state with the initial params state.
   */
  const enrichAndUpdateParams = useCallback(async () => {
    const parsedFilters: SearchFilters = convertFiltersStringToObject(
      urlParams.filters,
    );

    try {
      const shouldEnrich = parsedFilters.conditions.some(
        (condition) =>
          !isCustomAttributeCondition(condition) &&
          condition.values.some((value) => !value.label),
      );

      if (shouldEnrich) {
        const {
          data: { filters: enrichedFilters },
        } = await readSearchEnrichedFiltersQuery({
          filters: parsedFilters,
        }).unwrap();

        updateSearchParams(enrichedFilters);
      } else {
        updateSearchParams(parsedFilters);
      }

      // Even if the API request to enrich filters fails, we still fulfill with the partially parsed filters
      // It won't break the UI, but if any dynamic filters are set (e.g. agents or queues), their names won't be shown
    } catch (error) {
      updateSearchParams(parsedFilters);
    }
  }, [readSearchEnrichedFiltersQuery, updateSearchParams, urlParams.filters]);

  const init = useCallback(async () => {
    try {
      // Fetch the full list of custom attributes so that components further down can access them to
      // display the attribute label or selected filter value labels.
      await readCustomAttributes();

      await enrichAndUpdateParams();
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          urlParams,
        },
        tags: {
          team: SentryTeam.Insights,
        },
      });
    } finally {
      setIsInitialized(true);
    }
  }, [enrichAndUpdateParams, readCustomAttributes, urlParams]);

  useEffectOnce(() => {
    init();
  });

  // Handles re-initializing the Search state when the URL params change through navigating with the
  // browser's back/forward buttons. The check for params state equality is necessary because the
  // URL params can also change through a regular state update, which should not trigger a re-init.
  useUpdateEffect(() => {
    if (Object.is(params, prevParams)) {
      init();
    }
  }, [urlParams]);

  return { isInitialized: isInitialized };
}
